import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";
import type { TestHttpLog } from "@/pages/testing/shared";

/**
 * Test admin operací — admin API `/admin/api/v1/testing/admin/**`.
 * `/start` streamuje SSE (`event: progress`). Šest režimů, z toho
 * BULK_DEACTIVATE a LOG_CLEANUP jsou destruktivní (LOG_CLEANUP defaultně dry-run).
 */

export type AdminTestMode =
  | "TOKEN_SEARCH"
  | "BULK_DEACTIVATE"
  | "HEALTH_CHECK"
  | "LOG_EXPORT"
  | "LOG_STATISTICS"
  | "LOG_CLEANUP";

export const ADMIN_TEST_MODES: AdminTestMode[] = [
  "TOKEN_SEARCH",
  "BULK_DEACTIVATE",
  "HEALTH_CHECK",
  "LOG_EXPORT",
  "LOG_STATISTICS",
  "LOG_CLEANUP",
];

export const INSTANCE_STATES = ["WAITING_FOR_ACTIVATION", "ACTIVE", "TEMPORARY_BLOCKED", "BLOCKED", "DELETED"];

export interface TokenSearchEntry {
  tokenId: string;
  username: string | null;
  state: string;
  authServerType: string;
  createdAt: string | null;
  activatedAt: string | null;
}

export interface AuthServerStats {
  totalRequests: number | null;
  successCount: number | null;
  errorCount: number | null;
  avgResponseTimeMs: number | null;
  requestsLast24h: number | null;
}

export interface AdminResult {
  success: boolean;
  testMode: AdminTestMode;
  completedAt: string;
  durationMs: number;
  // TOKEN_SEARCH
  searchResults?: TokenSearchEntry[] | null;
  totalMatches?: number | null;
  // BULK_DEACTIVATE
  deactivatedCount?: number | null;
  failedCount?: number | null;
  deactivationErrors?: string[] | null;
  // HEALTH_CHECK
  healthStatus?: string | null;
  databaseStatus?: string | null;
  authServerHealth?: Record<string, string> | null;
  activeTokens?: number | null;
  // LOG_EXPORT
  exportSizeBytes?: number | null;
  exportedLogCount?: number | null;
  exportFormat?: string | null;
  // LOG_STATISTICS
  totalLogs?: number | null;
  errorsLast24h?: number | null;
  errorRateLast24h?: number | null;
  statsByAuthServer?: Record<string, AuthServerStats> | null;
  // LOG_CLEANUP
  deletedLogCount?: number | null;
  dryRun?: boolean | null;
  retentionDays?: number | null;
  httpLogs?: TestHttpLog[] | null;
}

export interface AdminProgress {
  status: string;
  message: string;
  timestamp: string;
  data?: AdminResult;
}

export interface AdminScenario {
  name: string;
  description: string;
  testMode: AdminTestMode;
  category: string;
  usernameFilter?: string | null;
  stateFilter?: string | null;
  authServerTypeFilter?: string | null;
  maxResults?: number | null;
  tokenIds?: string[] | null;
  deactivationReason?: string | null;
  bypassAuthServer?: boolean;
  exportFormat?: string | null;
  daysToExport?: number | null;
  retentionDays?: number | null;
  dryRun?: boolean;
}

export function useAdminScenarios() {
  return useQuery({
    queryKey: ["test-admin", "scenarios"],
    queryFn: () => apiFetch<AdminScenario[]>("/admin/api/v1/testing/admin/scenarios"),
  });
}
